import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { useNavigate } from 'react-router-dom';
import api from '../../api/api';
import { seatPrice, seatTotal } from '../../utils/orgPricing';
import { CreditCard, Users, Edit, Plus, Save, X, CheckCircle, XCircle, Search, Eye, Loader2, Ticket, Power, GraduationCap, UserCog, Trash2, Building2 } from 'lucide-react';

// Per-tier monthly usage limits are edited here on the Membership Tiers page
// (centralized) rather than on each feature's own admin page. These are the
// features that have a FeatureLimits row; keys must match FeatureName in the DB.
// `audience` matches a MembershipTypes.MembershipTypeName so the editor can grey
// out limits that don't apply to the tier's audience. Note the key is
// FeatureLimits.FeatureName, which is NOT always the matching Features.FeatureKey
// (the CSI retake limit is 'csi' while the premium feature is
// 'csi_recommendations'), so the audience is declared here rather than looked up.
// `featureKey` is the matching Features.FeatureKey — it drives the order of the
// "Unlocks features" picker so both lists read in the same order, and it isn't
// always the limit key (the CSI retake limit is 'csi', the premium feature is
// 'csi_recommendations').
const FEATURE_LIMIT_DEFS = [
    { key: 'resume_analysis', label: 'Resume Analysis', audience: 'Student', featureKey: 'resume_analysis' },
    { key: 'org_checklist', label: 'Resume Checklist', audience: 'Student', featureKey: 'org_checklist' },
    { key: 'mock_interview', label: 'Mock Interview', audience: 'Student', featureKey: 'mock_interview' },
    { key: 'elevator_pitch', label: 'Elevator Pitch', audience: 'Student', featureKey: 'elevator_pitch' },
    { key: 'csi', label: 'CSI', audience: 'Student', featureKey: 'csi_recommendations' },
    { key: 'coach_reporting', label: 'Coach Reporting', audience: 'Coach', featureKey: 'coach_reporting', metered: false },
    { key: 'my_program', label: 'My Program', audience: 'Coach', featureKey: 'my_program', metered: false },
];

// Canonical display order, shared by the feature picker and the limit fields.
const FEATURE_ORDER = FEATURE_LIMIT_DEFS.map((f) => f.featureKey);
const featureRank = (key) => {
    const i = FEATURE_ORDER.indexOf(key);
    return i === -1 ? FEATURE_ORDER.length : i; // anything unlisted sorts last
};

// Blank value for every defined limit, derived from the defs so adding a feature
// can't leave it out of form state (which would render an uncontrolled input).
const emptyLimits = () => Object.fromEntries(FEATURE_LIMIT_DEFS.map((f) => [f.key, '']));

export default function MembershipManagement() {
    const { user } = useAuth();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('student');
    const [tiers, setTiers] = useState([]);
    const [memberships, setMemberships] = useState([]);
    const [membershipTypes, setMembershipTypes] = useState([]);
    const [features, setFeatures] = useState([]);
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // Save errors for the open tier / code modal: the page banner sits behind its overlay
    const [modalError, setModalError] = useState('');
    const [editingTier, setEditingTier] = useState(null);
    const [showTierModal, setShowTierModal] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [showCodeModal, setShowCodeModal] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [pagination, setPagination] = useState({ page: 1, per_page: 20, total: 0 });
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showExpired, setShowExpired] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        if (!user || !user.isAdmin) {
            navigate('/userdashboard');
        }
    }, [user, navigate]);

    const fetchTiers = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/membership/admin/tiers');
            setTiers(response.data.tiers || []);
        } catch (err) {
            console.error('Error fetching tiers:', err);
            setError('Failed to load membership tiers');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchMemberships = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: pagination.page,
                per_page: pagination.per_page,
            });
            if (debouncedSearch) params.set('search', debouncedSearch);
            if (showExpired) params.set('show_expired', 'true');
            const response = await api.get(`/membership/admin/memberships?${params.toString()}`);
            setMemberships(response.data.memberships || []);
            setPagination(prev => ({ ...prev, total: response.data.total || 0, total_pages: response.data.total_pages || 1 }));
        } catch (err) {
            console.error('Error fetching memberships:', err);
            setError('Failed to load active memberships');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.per_page, debouncedSearch, showExpired]);

    // Audience types back the tier modal's checkboxes — load them once up front.
    const fetchTypes = useCallback(async () => {
        try {
            const response = await api.get('/membership/admin/membership-types');
            setMembershipTypes(response.data.types || []);
        } catch (err) {
            console.error('Error fetching membership types:', err);
        }
    }, []);

    const fetchFeatures = useCallback(async () => {
        try {
            const response = await api.get('/membership/admin/features');
            setFeatures(response.data.features || []);
        } catch (err) {
            console.error('Error fetching features:', err);
        }
    }, []);

    const fetchCodes = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/membership/admin/referral-codes');
            setCodes(response.data.codes || []);
        } catch (err) {
            console.error('Error fetching referral codes:', err);
            setError('Failed to load referral codes');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchTypes(); fetchFeatures(); }, [fetchTypes, fetchFeatures]);

    useEffect(() => {
        if (['student', 'coach', 'tiers'].includes(activeTab)) fetchTiers();
        else if (activeTab === 'members') fetchMemberships();
        else if (activeTab === 'codes') fetchCodes();
    }, [activeTab, pagination.page, fetchTiers, fetchMemberships, fetchCodes]);

    React.useEffect(() => {
        if (!showTierModal && !showCodeModal) setModalError('');
    }, [showTierModal, showCodeModal]);

    const flashSuccess = (msg) => {
        setSuccess(msg);
        setTimeout(() => setSuccess(''), 3000);
    };

    // Upsert per-feature monthly usage limits for a tier. Reuses the existing
    // /admin/feature-limits endpoints: PUT when the tier already has a row for
    // that feature, POST to create otherwise. A blank value means "leave
    // unchanged" (there is no delete endpoint for a limit).
    const saveFeatureLimits = async (tierId, featureLimits) => {
        for (const fl of featureLimits) {
            if (fl.value === '' || fl.value === null || fl.value === undefined) continue;
            //const monthlyLimit = parseInt(fl.value, 10);
            // The limit column is an INT, so anything bigger is saved as its max
            const monthlyLimit = Math.min(parseInt(fl.value, 10), 2147483647);
            if (isNaN(monthlyLimit) || monthlyLimit < 0) continue;
            if (fl.id) {
                await api.put(`/admin/feature-limits/${fl.id}`, { monthlyLimit });
            } else {
                //await api.post('/admin/feature-limits', {
                //    tierID: tierId, featureName: fl.feature, monthlyLimit,
                //});
                try {
                    await api.post('/admin/feature-limits', {
                        tierID: tierId, featureName: fl.feature, monthlyLimit,
                    });
                } catch (err) {
                    // Already saved by an earlier Save that failed partway: update that row
                    if (err.response?.status !== 409) throw err;
                    const res = await api.get('/admin/feature-limits', { params: { feature: fl.feature } });
                    const row = (res.data.limits || []).find((r) => r.TierID === tierId);
                    if (!row) throw err;
                    await api.put(`/admin/feature-limits/${row.FeatureLimitID}`, { monthlyLimit });
                }
            }
        }
    };

    const handleSaveTier = async (tierData) => {
        try {
            setSaving(true);
            setError('');
            setModalError('');
            setSuccess('');
            // feature_limits rides along on tierData but isn't a tier column; split it out.
            const { feature_limits, ...tierPayload } = tierData;
            let tierId;
            if (editingTier) {
                await api.put(`/membership/admin/tiers/${editingTier.TierID}`, tierPayload);
                tierId = editingTier.TierID;
                flashSuccess('Tier updated successfully!');
            } else {
                const res = await api.post('/membership/admin/tiers', tierPayload);
                tierId = res.data?.tier_id;
                // The tier exists now: if the limits below fail, Save again
                // updates this tier instead of creating a second one
                if (tierId) setEditingTier({ TierID: tierId, TierName: tierPayload.tier_name });
                flashSuccess('Tier created successfully!');
            }
            // Centralized usage limits: set each feature's monthly quota in the same save.
            if (tierId && Array.isArray(feature_limits)) {
                await saveFeatureLimits(tierId, feature_limits);
            }
            setShowTierModal(false);
            setEditingTier(null);
            fetchTiers();
        } catch (err) {
            //setError(err.response?.data?.error || 'Failed to save tier');
            setModalError(err.response?.data?.error || 'Failed to save tier');
            // Part of the save may have gone through (the tier, some limits)
            fetchTiers();
        } finally {
            setSaving(false);
        }
    };

    const handleSaveCode = async (codeData) => {
        try {
            setSaving(true);
            setError('');
            setModalError('');
            if (editingCode) {
                await api.put(`/membership/admin/referral-codes/${editingCode.MembershipReferralCodeID}`, codeData);
                flashSuccess('Referral code updated!');
            } else {
                await api.post('/membership/admin/referral-codes', codeData);
                flashSuccess('Referral code created!');
            }
            setShowCodeModal(false);
            setEditingCode(null);
            fetchCodes();
        } catch (err) {
            //setError(err.response?.data?.error || 'Failed to save referral code');
            setModalError(err.response?.data?.error || 'Failed to save referral code');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleCode = async (code) => {
        try {
            await api.patch(`/membership/admin/referral-codes/${code.MembershipReferralCodeID}/status`, { is_active: !code.isActive });
            fetchCodes();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update referral code');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const isExpired = (endDate) => new Date(endDate) < new Date();

    const openNewTier = () => { setEditingTier(null); setShowTierModal(true); };
    const openEditTier = (tier) => { setEditingTier(tier); setShowTierModal(true); };

    const hasAudience = (tier, name) => (tier.Audiences || []).includes(name);
    const studentTiers = tiers.filter(t => t.IsActive && hasAudience(t, 'Student'));
    const coachTiers = tiers.filter(t => t.IsActive && hasAudience(t, 'Coach'));

    const tabDef = [
        { key: 'student', label: 'Active Student Membership', Icon: GraduationCap },
        { key: 'coach', label: 'Active Coach Membership', Icon: UserCog },
        { key: 'tiers', label: 'Membership Tiers', Icon: CreditCard },
        { key: 'codes', label: 'Referral Codes', Icon: Ticket },
        { key: 'members', label: 'Active Members', Icon: Users },
    ];

    if (loading && tiers.length === 0 && memberships.length === 0 && codes.length === 0) {
        return (
            <div className={`flex items-center justify-center min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    const audienceBadge = (name) => {
        const map = {
            Student: 'bg-sky-100 text-sky-800',
            Coach: 'bg-indigo-100 text-indigo-800',
            Enterprise: 'bg-amber-100 text-amber-800',
        };
        return map[name] || 'bg-slate-100 text-slate-700';
    };

    const TierCard = ({ tier }) => (
        <div className={`border rounded-xl p-4 hover:shadow-md transition-shadow ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h3 className={`text-lg font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{tier.TierName}</h3>
                        {tier.IsActive ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">Active</span>
                        ) : (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded">Inactive</span>
                        )}
                        {(tier.Audiences || []).map((a) => (
                            <span key={a} className={`px-2 py-1 text-xs font-semibold rounded ${audienceBadge(a)}`}>{a}</span>
                        ))}
                        {(!tier.Audiences || tier.Audiences.length === 0) && (
                            <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs font-semibold rounded">No audience</span>
                        )}
                        {/* Only one tier holds this, so surface it in the list —
                            otherwise you'd have to open each tier to find it. */}
                        {(tier.IsReferralReward === true || tier.IsReferralReward === 1) && (
                            <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded">🎁 Referral reward</span>
                        )}
                        {Boolean(tier.SoldToOrganizations) && (
                            <span className="px-2 py-1 bg-violet-100 text-violet-800 text-xs font-semibold rounded">🏢 Group pricing</span>
                        )}
                    </div>
                    <p className={`mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{tier.TierDescription || 'No description'}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                            <span className="text-slate-500">Price:</span>
                            <span className="ml-2 font-semibold">${tier.Price != null ? Number(tier.Price).toFixed(2) : '0.00'}</span>
                        </div>
                        <div>
                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Duration:</span>
                            <span className={`ml-2 font-semibold ${isDarkMode ? 'text-slate-200' : ''}`}>{tier.DurationDays} days</span>
                        </div>
                        {Number(tier.Price) > 0 && (
                            <div>
                                <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Billing:</span>
                                <span className={`ml-2 font-semibold ${isDarkMode ? 'text-slate-200' : ''}`}>{tier.BillingFrequency}</span>
                            </div>
                        )}
                        {tier.PaymentLink && (
                            <div>
                                <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Price ID:</span>
                                <span className={`ml-2 font-mono text-xs ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>{tier.PaymentLink}</span>
                            </div>
                        )}
                    </div>
                    {/* Group pricing: what an organization pays per seat at each volume step */}
                    {Boolean(tier.SoldToOrganizations) && (
                        <div className={`mt-3 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Group pricing:</span>
                            <span className="ml-2">
                                {tier.GroupDiscounts?.length > 0
                                    ? tier.GroupDiscounts.map((d) => (
                                        //`${d.MinQuantity}+ licenses → $${(Number(tier.Price) * (100 - d.PercentOff) / 100).toFixed(2)} each (${Math.round(d.PercentOff * 100) / 100}% off)`
                                        `${d.MinQuantity}+ licenses → $${seatPrice(tier.Price, d.PercentOff).toFixed(2)} each (${Math.round(d.PercentOff * 100) / 100}% off)`
                                    )).join('  ·  ')
                                    : `$${Number(tier.Price).toFixed(2)} per license / ${tier.BillingFrequency === 'yearly' ? 'year' : 'month'}, no volume discount`}
                            </span>
                        </div>
                    )}
                </div>
                <button
                    onClick={() => openEditTier(tier)}
                    className={`ml-4 p-2 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:text-blue-400 hover:bg-slate-700' : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'}`}
                >
                    <Edit size={18} />
                </button>
            </div>
        </div>
    );

    const tierListPanel = (title, subtitle, list, showNew) => (
        <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{title}</h2>
                    {subtitle && <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{subtitle}</p>}
                </div>
                {showNew && (
                    <button onClick={openNewTier} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                        <Plus size={18} /> New Tier
                    </button>
                )}
            </div>
            <div className="space-y-4">
                {list.map((tier) => <TierCard key={tier.TierID} tier={tier} />)}
                {list.length === 0 && !loading && (
                    <p className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No tiers to show.</p>
                )}
            </div>
        </div>
    );

    return (
        <div className={`min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <header className="mb-8">
                    <h1 className={`text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Membership Management</h1>
                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Manage membership tiers by audience, referral codes, and view active members</p>
                </header>

                {/* Tabs */}
                <div className={`mb-6 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <div className="flex gap-4 flex-wrap">
                        {tabDef.map(({ key, label, Icon }) => (
                            <button
                                key={key}
                                onClick={() => setActiveTab(key)}
                                className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
                                    activeTab === key
                                        ? 'border-blue-600 text-blue-600'
                                        : isDarkMode
                                            ? 'border-transparent text-slate-400 hover:text-slate-200'
                                            : 'border-transparent text-slate-600 hover:text-slate-900'
                                }`}
                            >
                                <Icon size={18} className="inline mr-2" />
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Messages */}
                {error && (
                    <div className={`mb-4 p-4 border rounded-lg ${isDarkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-800'}`}>
                        {error}
                    </div>
                )}
                {success && (
                    <div className={`mb-4 p-4 border rounded-lg ${isDarkMode ? 'bg-green-900/30 border-green-800 text-green-300' : 'bg-green-50 border-green-200 text-green-800'}`}>
                        {success}
                    </div>
                )}

                {/* Active Student Membership */}
                {activeTab === 'student' && tierListPanel(
                    'Active Student Membership',
                    'Active tiers available to students.',
                    studentTiers,
                    false,
                )}

                {/* Active Coach Membership */}
                {activeTab === 'coach' && tierListPanel(
                    'Active Coach Membership',
                    'Active tiers available to coaches.',
                    coachTiers,
                    false,
                )}

                {/* Membership Tiers — all tiers, active and inactive, full edit */}
                {activeTab === 'tiers' && tierListPanel(
                    'Membership Tiers',
                    'All memberships — active and inactive. Create and edit tiers here.',
                    tiers,
                    true,
                )}

                {/* Referral Codes Tab */}
                {activeTab === 'codes' && (
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Referral Codes</h2>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Codes that grant a membership tier (e.g. a free coach account).</p>
                            </div>
                            <button
                                onClick={() => { setEditingCode(null); setShowCodeModal(true); }}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                            >
                                <Plus size={18} />
                                New Code
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className={isDarkMode ? 'bg-slate-700' : 'bg-slate-50'}>
                                    <tr>
                                        {['Code', 'Grants Tier', 'Discount', 'Redeemed', 'Expires', 'Status', ''].map((h, i) => (
                                            <th key={i} className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
                                    {codes.map((c) => (
                                        <tr key={c.MembershipReferralCodeID} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}>
                                            <td className={`px-4 py-3 font-mono font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{c.Code}</td>
                                            <td className="px-4 py-3 text-sm">
                                                <div className="flex flex-wrap gap-1">
                                                    {(c.Tiers || []).map(t => (
                                                        <span key={t.TierID} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">{t.TierName}</span>
                                                    ))}
                                                    {(!c.Tiers || c.Tiers.length === 0) && <span className="text-xs text-slate-400">—</span>}
                                                </div>
                                            </td>
                                            <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{describeDiscount(c)}</td>
                                            <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                {c.TimesRedeemed || 0}{c.MaxRedemptions != null ? ` / ${c.MaxRedemptions}` : ''}
                                            </td>
                                            <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                {c.ExpiresAt ? new Date(c.ExpiresAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                                            </td>
                                            <td className="px-4 py-3">
                                                {/* {c.ExpiresAt && new Date(c.ExpiresAt) < new Date() ? ( */}
                                                {/* Same rule as redeeming: the code works through the end of its date */}
                                                {(c.IsExpired !== undefined ? Boolean(c.IsExpired) : (c.ExpiresAt && new Date(c.ExpiresAt) < new Date())) ? (
                                                    <span className="flex items-center gap-1 text-red-500"><XCircle size={16} /><span className="text-xs font-semibold">Expired</span></span>
                                                ) : c.isActive ? (
                                                    <span className="flex items-center gap-1 text-green-600"><CheckCircle size={16} /><span className="text-xs font-semibold">Active</span></span>
                                                ) : (
                                                    <span className="flex items-center gap-1 text-slate-500"><XCircle size={16} /><span className="text-xs font-semibold">Inactive</span></span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleToggleCode(c)}
                                                        title={c.isActive ? 'Deactivate' : 'Activate'}
                                                        className={`p-2 rounded-lg transition-colors ${c.isActive ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'} ${isDarkMode ? 'hover:bg-slate-700' : ''}`}
                                                    >
                                                        <Power size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => { setEditingCode(c); setShowCodeModal(true); }}
                                                        className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:text-blue-400 hover:bg-slate-700' : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'}`}
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {codes.length === 0 && !loading && (
                                        <tr><td colSpan={7} className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No referral codes yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Members Tab */}
                {activeTab === 'members' && (
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Active Members</h2>
                        </div>

                        {/* Filters */}
                        <div className="flex flex-wrap items-center gap-4 mb-6">
                            <div className="relative flex-1 min-w-[200px] max-w-md">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search by name or email..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setPagination(prev => ({ ...prev, page: 1 }));
                                    }}
                                    className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500' : 'border-slate-300'}`}
                                />
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={showExpired}
                                    onChange={(e) => {
                                        setShowExpired(e.target.checked);
                                        setPagination(prev => ({ ...prev, page: 1 }));
                                    }}
                                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Show expired</span>
                            </label>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className={isDarkMode ? 'bg-slate-700' : 'bg-slate-50'}>
                                    <tr>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>User</th>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Tier</th>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Start Date</th>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>End Date</th>
                                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Status</th>
                                        <th className="px-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
                                    {memberships.map((membership) => (
                                        <tr key={membership.MembershipID} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}>
                                            <td className="px-4 py-3">
                                                <div>
                                                    <div className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                                        {membership.FirstName} {membership.LastName}
                                                    </div>
                                                    <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{membership.EmailAddress}</div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">{membership.TierName}</span>
                                            </td>
                                            <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{formatDate(membership.StartDate)}</td>
                                            <td className="px-4 py-3">
                                                <div className={`text-sm ${isExpired(membership.EndDate) ? 'text-red-600 font-semibold' : isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                    {formatDate(membership.EndDate)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                {!membership.isActive ? (
                                                    <span className="flex items-center gap-1 text-slate-500">
                                                        <XCircle size={16} />
                                                        <span className="text-xs font-semibold">Inactive</span>
                                                    </span>
                                                ) : isExpired(membership.EndDate) ? (
                                                    <span className="flex items-center gap-1 text-red-600">
                                                        <XCircle size={16} />
                                                        <span className="text-xs font-semibold">Expired</span>
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1 text-green-600"><CheckCircle size={16} /><span className="text-xs font-semibold">Active</span></span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <button
                                                    onClick={async () => {
                                                        setLoadingDetails(true);
                                                        try {
                                                            const res = await api.get(`/membership/admin/memberships/${membership.MembershipID}/payment-details`);
                                                            setPaymentDetails(res.data);
                                                        } catch (err) {
                                                            setError('Failed to load payment details');
                                                        } finally {
                                                            setLoadingDetails(false);
                                                        }
                                                    }}
                                                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                                                >
                                                    <Eye size={14} />
                                                    Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination.total_pages > 1 && (
                            <div className="mt-6 flex items-center justify-between">
                                <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                    Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
                                    {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} members
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page === 1}
                                        className={`px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page >= pagination.total_pages}
                                        className={`px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Payment Details Modal */}
                        {(paymentDetails || loadingDetails) && (
                            <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ zIndex: 200 }} onClick={() => !loadingDetails && setPaymentDetails(null)}>
                                <div className={`rounded-2xl shadow-xl max-w-lg w-full mx-4 overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
                                    <div className={`flex justify-between items-center px-6 py-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                        <h3 className={`text-lg font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Payment Details</h3>
                                        <button onClick={() => setPaymentDetails(null)} className={`p-1 rounded-lg ${isDarkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}>
                                            <X size={20} />
                                        </button>
                                    </div>
                                    {loadingDetails ? (
                                        <div className="flex items-center justify-center py-12">
                                            <Loader2 size={24} className="animate-spin text-blue-500" />
                                        </div>
                                    ) : paymentDetails && (
                                        <div className="px-6 py-4 space-y-4">
                                            <div className="grid grid-cols-2 gap-3 text-sm">
                                                <div>
                                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Member</span>
                                                    <p className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{paymentDetails.FirstName} {paymentDetails.LastName}</p>
                                                </div>
                                                <div>
                                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Email</span>
                                                    <p className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{paymentDetails.EmailAddress}</p>
                                                </div>
                                                <div>
                                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Tier</span>
                                                    <p className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{paymentDetails.TierName}</p>
                                                </div>
                                                <div>
                                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Price</span>
                                                    <p className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                                        ${paymentDetails.Price != null ? Number(paymentDetails.Price).toFixed(2) : 'N/A'}
                                                        {paymentDetails.BillingFrequency && <span className={`font-normal ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}> / {paymentDetails.BillingFrequency}</span>}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Payment Date</span>
                                                    <p className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{formatDate(paymentDetails.PaymentDate)}</p>
                                                </div>
                                                <div>
                                                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Payment ID</span>
                                                    <p className={`font-semibold font-mono text-xs ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{paymentDetails.PaymentID}</p>
                                                </div>
                                            </div>

                                            {/* Stripe Details */}
                                            <div className={`border-t pt-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                                <h4 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Stripe Details</h4>
                                                <div className="grid grid-cols-2 gap-3 text-sm">
                                                    {paymentDetails.stripe_status && (
                                                        <div>
                                                            <span className="text-slate-500">Subscription Status</span>
                                                            <p className={`font-semibold ${paymentDetails.stripe_status === 'active' ? 'text-green-600' : paymentDetails.stripe_status === 'canceled' ? 'text-red-600' : 'text-slate-900'}`}>
                                                                {paymentDetails.stripe_status}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {paymentDetails.stripe_payment_status && (
                                                        <div>
                                                            <span className="text-slate-500">Payment Status</span>
                                                            <p className={`font-semibold ${paymentDetails.stripe_payment_status === 'paid' ? 'text-green-600' : 'text-slate-900'}`}>
                                                                {paymentDetails.stripe_payment_status}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {paymentDetails.stripe_amount_total != null && (
                                                        <div>
                                                            <span className="text-slate-500">Amount Charged</span>
                                                            <p className="font-semibold text-slate-900">
                                                                ${(paymentDetails.stripe_amount_total / 100).toFixed(2)} {paymentDetails.stripe_currency?.toUpperCase()}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {paymentDetails.stripe_cancel_at_period_end && (
                                                        <div className="col-span-2">
                                                            <span className="text-amber-600 text-xs font-semibold">Cancels at end of period</span>
                                                        </div>
                                                    )}
                                                    {paymentDetails.StripeSubscriptionID && (
                                                        <div className="col-span-2">
                                                            <span className="text-slate-500">Subscription ID</span>
                                                            <p className="font-mono text-xs text-slate-700 break-all">{paymentDetails.StripeSubscriptionID}</p>
                                                        </div>
                                                    )}
                                                    {paymentDetails.VerificationID && (
                                                        <div className="col-span-2">
                                                            <span className="text-slate-500">Session ID</span>
                                                            <p className="font-mono text-xs text-slate-700 break-all">{paymentDetails.VerificationID}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Tier create/edit modal */}
            {showTierModal && (
                <ModalShell title={editingTier ? 'Edit Tier' : 'New Tier'} onClose={() => { setShowTierModal(false); setEditingTier(null); }} isDarkMode={isDarkMode}>
                    <TierEditForm
                        tier={editingTier}
                        types={membershipTypes}
                        allTiers={tiers}
                        features={features}
                        onSave={handleSaveTier}
                        onCancel={() => { setShowTierModal(false); setEditingTier(null); }}
                        saving={saving}
                        error={modalError}
                    />
                </ModalShell>
            )}

            {/* Referral code create/edit modal */}
            {showCodeModal && (
                <ModalShell title={editingCode ? 'Edit Referral Code' : 'New Referral Code'} onClose={() => { setShowCodeModal(false); setEditingCode(null); }} isDarkMode={isDarkMode}>
                    <CodeEditForm
                        code={editingCode}
                        tiers={tiers}
                        onSave={handleSaveCode}
                        onCancel={() => { setShowCodeModal(false); setEditingCode(null); }}
                        saving={saving}
                        error={modalError}
                    />
                </ModalShell>
            )}
        </div>
    );
}

function describeDiscount(c) {
    if (!c) return '';
    if (c.DiscountType === 'free') return 'Free';
    if (c.DiscountType === 'percent') return `${Number(c.DiscountValue || 0)}% off`;
    if (c.DiscountType === 'amount') return `$${Number(c.DiscountValue || 0).toFixed(2)} off`;
    return c.DiscountType;
}

// Shared centered modal shell (matches the Payment Details modal styling).
function ModalShell({ title, onClose, isDarkMode, children }) {
    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" style={{ zIndex: 200 }} onClick={onClose}>
            <div className={`rounded-2xl shadow-xl max-w-2xl w-full my-8 max-h-[90vh] flex flex-col overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
                <div className={`flex-none flex justify-between items-center px-6 py-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{title}</h3>
                    <button onClick={onClose} className={`p-1 rounded-lg ${isDarkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}>
                        <X size={20} />
                    </button>
                </div>
                <div className="px-6 py-5 overflow-y-auto">
                    {children}
                </div>
            </div>
        </div>
    );
}

//function TierEditForm({ tier, types, features, allTiers, onSave, onCancel, saving }) {
function TierEditForm({ tier, types, features, allTiers, onSave, onCancel, saving, error }) {
    const { isDarkMode } = useTheme();
    const [formData, setFormData] = React.useState({
        tier_name: tier?.TierName || '',
        tier_description: tier?.TierDescription || '',
        payment_link: tier?.PaymentLink || '',
        // Number boxes keep what the admin types (so a box can be cleared and
        // retyped); they're checked and converted on save.
        price: tier?.Price != null ? String(Number(tier.Price)) : '0',
        billing_frequency: tier?.BillingFrequency || 'monthly',
        duration_days: String(tier?.DurationDays || 30),
        is_active: tier?.IsActive !== undefined ? (tier.IsActive ? 1 : 0) : 1,
        show_in_onboarding: tier?.ShowInOnboarding !== undefined ? Boolean(tier.ShowInOnboarding) : false,
        open_for_enrollment: tier?.OpenForEnrollment !== undefined ? Boolean(tier.OpenForEnrollment) : true,
        is_most_popular: tier?.IsMostPopular !== undefined ? Boolean(tier.IsMostPopular) : false,
        is_referral_reward: tier?.IsReferralReward !== undefined ? Boolean(tier.IsReferralReward) : false,
        trial_days: tier?.TrialDays || '',
    });
    const [audienceIds, setAudienceIds] = React.useState(tier?.AudienceIds || []);
    const [featureIds, setFeatureIds] = React.useState(tier?.FeatureIds || []);
    // Organization sales: seats cost this tier's own price, less a volume discount.
    // Each discount row keeps the typed text of both boxes (percent / price each)
    // and the exact percent that gets saved.
    const [group, setGroup] = React.useState({
        enabled: Boolean(tier?.SoldToOrganizations),
        discounts: (tier?.GroupDiscounts || []).map((d) => ({ min_quantity: d.MinQuantity, pct: d.PercentOff, stripe_price_id: d.StripePriceID || '' })),
    });
    // Narrow boxes for the discount rows: one width class each (w-full would stretch them)
    const boxCls = (width) => `${width} p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`;
    const setDiscount = (i, changes) => setGroup((g) => ({
        ...g, discounts: g.discounts.map((d, j) => (j === i ? { ...d, ...changes } : d)),
    }));
    // The tier's price changed: show each discount's price again from its percent
    // (the percent is what's saved), not a price typed against the old price.
    React.useEffect(() => {
        setGroup((g) => ({ ...g, discounts: g.discounts.map(({ priceText, ...d }) => d) }));
    }, [formData.price]);
    // Per-feature monthly usage limits for this tier (value = quota, id = existing row).
    const [limits, setLimits] = React.useState(emptyLimits);
    const [limitIds, setLimitIds] = React.useState({});

    // Prefill the tier's current limits. The GET filters by one feature and
    // returns every tier's row for it, so fetch each feature and pick ours.
    React.useEffect(() => {
        if (!tier?.TierID) return;
        let cancelled = false;
        (async () => {
            try {
                const results = await Promise.all(
                    FEATURE_LIMIT_DEFS.map((f) =>
                        api.get('/admin/feature-limits', { params: { feature: f.key } })
                    )
                );
                if (cancelled) return;
                const vals = {}; const ids = {};
                FEATURE_LIMIT_DEFS.forEach((f, i) => {
                    const row = (results[i].data.limits || []).find((r) => r.TierID === tier.TierID);
                    if (row) { vals[f.key] = row.MonthlyLimit; ids[f.key] = row.FeatureLimitID; }
                });
                setLimits((prev) => ({ ...prev, ...vals }));
                setLimitIds(ids);
            } catch (e) {
                // Non-fatal: limits just start blank if they can't be loaded.
            }
        })();
        return () => { cancelled = true; };
    }, [tier]);

    // Which other tier currently holds the referral reward, if any — only one
    // tier can, so the form says where it is now.
    const rewardHolder = (allTiers || []).find(
        (t) => (t.IsReferralReward === true || t.IsReferralReward === 1) && t.TierID !== tier?.TierID
    );

    const priceNum = formData.price === '' ? null : Number(formData.price);
    const durationNum = Number(formData.duration_days);
    const priceProblem = priceNum === null || Number.isNaN(priceNum)
        ? 'Enter a price (0 for a free tier).'
        : priceNum < 0 ? "Price can't be negative." : '';
    const durationProblem = formData.duration_days === '' || !Number.isInteger(durationNum) || durationNum < 1
        ? 'Enter how many days a membership lasts (1 or more).' : '';
    const trialProblem = formData.trial_days !== '' && Number(formData.trial_days) < 0 ? "Trial days can't be negative." : '';
    const isFreeForever = priceNum === 0 && !formData.trial_days;
    const priceIsZero = priceNum === 0;
    const activeTypes = (types || []).filter(t => t.isActive || audienceIds.includes(t.MembershipTypeID));
    // Audience names this tier targets — drives which usage limits are editable.
    const audienceNames = (types || [])
        .filter((t) => audienceIds.includes(t.MembershipTypeID))
        .map((t) => t.MembershipTypeName);
    // A friend referral gives both students a week of this tier, so it has to
    // be an active, paid tier open to Students (the server checks the same)
    const rewardBlockedReason = !formData.is_active
        ? 'Only an active tier can be the referral reward.'
        : !audienceNames.includes('Student')
            ? 'Pick Student under “Available to” to use this tier as the referral reward.'
            : !(priceNum > 0)
                ? "A free tier can't be the referral reward."
                : '';

    const toggleAudience = (id) => {
        setAudienceIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };
    const toggleFeature = (id) => {
        setFeatureIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    // Group features by category for the "Unlocks features" picker.
    const featureGroups = {};
    (features || []).forEach((f) => {
        const cat = f.Category || 'General';
        (featureGroups[cat] = featureGroups[cat] || []).push(f);
    });
    // Match the Usage limits order below, so the two lists line up.
    Object.values(featureGroups).forEach((list) =>
        list.sort((a, b) => featureRank(a.FeatureKey) - featureRank(b.FeatureKey))
    );

    const sellsToOrgs = audienceNames.includes('Student') || audienceNames.includes('Coach');
    const planPrice = priceNum || 0;
    // Free and one-time tiers can't be sold to organizations: seats are billed
    // on the tier's own cycle and renew automatically.
    // The section is always shown so admins can find it; when a tier can't be
    // sold to organizations the switch is off and says why.
    //const orgBlockedReason = !sellsToOrgs
    const orgBlockedReason = !formData.is_active
        ? "Inactive tiers can't be sold to organizations."
        : !sellsToOrgs
        ? "Pick Student or Coach under “Available to” first."
        : planPrice <= 0
            ? "Free tiers can't be sold to organizations."
            : formData.billing_frequency === 'one-time'
                ? "Only monthly or yearly tiers can be sold to organizations (seats renew automatically)."
                : !formData.payment_link.trim().startsWith('price_')
                    ? "This tier needs a Stripe Price ID (price_...) first: seats without a discount are paid with it."
                    : '';
    const groupOn = group.enabled && !orgBlockedReason;
    const perPeriod = formData.billing_frequency === 'yearly' ? 'year' : 'month';
    const priceAfter = (pct) => seatPrice(planPrice, pct);
    const pctText = (pct) => String(Math.round(pct * 100) / 100);
    // A row the admin touched must be valid; a fully blank row is just ignored.
    const isBlankRow = (d) => d.min_quantity === '' && !d.pct && !d.pctText && !d.priceText && !d.stripe_price_id;
    const discountProblems = group.discounts.map((d, i) => {
        if (isBlankRow(d)) return '';
        const min = Number(d.min_quantity);
        if (d.min_quantity === '' || !Number.isInteger(min) || min < 2) return 'Start at 2 or more licenses.';
        if (group.discounts.some((o, j) => j < i && Number(o.min_quantity) === min)) return 'Another discount already starts at this number.';
        if (!(d.pct > 0 && d.pct < 100)) return `Use more than 0% and less than 100% off (a price between $0 and $${planPrice.toFixed(2)}).`;
        // Buying more never costs more per license
        const smaller = group.discounts.filter((o) => !isBlankRow(o) && Number(o.min_quantity) < min && o.pct >= d.pct)
            .sort((x, y) => Number(y.min_quantity) - Number(x.min_quantity))[0];
        if (smaller) return `Give more than the ${smaller.min_quantity}+ discount (${pctText(smaller.pct)}% off).`;
        if (!(d.stripe_price_id || '').trim().startsWith('price_')) return `Add the Stripe Price ID for this price ($${priceAfter(d.pct).toFixed(2)} per ${perPeriod}).`;
        return '';
    });
    const groupDiscounts = group.discounts.filter((d) => !isBlankRow(d));
    // Example for the preview line: the biggest discount step, or 10 licenses.
    const exampleQty = groupDiscounts.length ? Math.max(...groupDiscounts.map((d) => Number(d.min_quantity))) : 10;
    const examplePct = groupDiscounts.filter((d) => exampleQty >= Number(d.min_quantity))
        .reduce((best, d) => (Number(d.min_quantity) > best.min ? { min: Number(d.min_quantity), pct: d.pct } : best), { min: 0, pct: 0 }).pct;
    const exampleTotal = seatTotal(seatPrice(planPrice, examplePct), exampleQty);

    const submit = () => onSave({
        ...formData,
        price: priceNum,
        duration_days: durationNum,
        payment_link: formData.payment_link.trim(),
        membership_type_ids: audienceIds,
        feature_ids: featureIds,
        sold_to_organizations: groupOn,
        group_discounts: groupOn ? groupDiscounts.map((d) => ({
            min_quantity: Number(d.min_quantity), percent_off: Math.round(d.pct * 10000) / 10000,
            stripe_price_id: (d.stripe_price_id || '').trim(),
        })) : [],
        // Only send limits for audiences this tier actually targets — a Coach-only
        // tier has no business writing Student feature quotas (that's how a stray
        // Mock Interview limit ended up on a coach tier).
        feature_limits: FEATURE_LIMIT_DEFS.filter((f) => audienceNames.includes(f.audience)).map((f) => ({
            feature: f.key, value: limits[f.key], id: limitIds[f.key] || null,
        })),
    });

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Tier Name *</label>
                    <input
                        type="text"
                        value={formData.tier_name}
                        onChange={(e) => setFormData({ ...formData, tier_name: e.target.value })}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                        placeholder="e.g., Coach Pro, Student Pro"
                    />
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Status</label>
                    <select
                        value={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                        className={`select-styled w-full text-sm font-medium rounded-xl px-3 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
                    >
                        <option value={1}>Active</option>
                        <option value={0}>Inactive</option>
                    </select>
                    {/* Organizations sponsor this tier: say what turning it off does to them */}
                    {!formData.is_active && Boolean(tier?.SoldToOrganizations) && (
                        <p className={`mt-1 text-xs ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                            Organizations buy seats of this tier. Once it's inactive they can't buy more;
                            seats they already have keep this tier.
                        </p>
                    )}
                </div>
            </div>

            {/* Audience — any combination of Student / Coach / Enterprise */}
            <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Available to *</label>
                <div className="flex flex-wrap gap-2">
                    {activeTypes.map((t) => {
                        const on = audienceIds.includes(t.MembershipTypeID);
                        return (
                            <button
                                type="button"
                                key={t.MembershipTypeID}
                                onClick={() => toggleAudience(t.MembershipTypeID)}
                                className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                                    on
                                        ? 'bg-blue-600 border-blue-600 text-white'
                                        : isDarkMode ? 'border-slate-600 text-slate-300 hover:border-slate-500' : 'border-slate-300 text-slate-600 hover:border-slate-400'
                                }`}
                            >
                                {on ? '✓ ' : ''}{t.MembershipTypeName}
                            </button>
                        );
                    })}
                    {activeTypes.length === 0 && (
                        <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No audience types available.</span>
                    )}
                </div>
                <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Pick one or more. Students see Student tiers, coaches see Coach tiers.</p>
            </div>

            {/* Feature Access — which features this tier unlocks, grouped by category */}
            <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Unlocks features</label>
                {Object.keys(featureGroups).length === 0 ? (
                    <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No features defined yet.</span>
                ) : (
                    <div className="space-y-2.5">
                        {Object.keys(featureGroups).sort().map((cat) => (
                            <div key={cat}>
                                <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{cat}</div>
                                <div className="flex flex-wrap gap-2">
                                    {featureGroups[cat].map((f) => {
                                        const on = featureIds.includes(f.FeatureID);
                                        return (
                                            <button
                                                type="button"
                                                key={f.FeatureID}
                                                onClick={() => toggleFeature(f.FeatureID)}
                                                title={f.Description || ''}
                                                className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                                                    on
                                                        ? 'bg-primary-600 border-primary-600 text-white'
                                                        : isDarkMode ? 'border-slate-600 text-slate-300 hover:border-slate-500' : 'border-slate-300 text-slate-600 hover:border-slate-400'
                                                }`}
                                            >
                                                {on ? '✓ ' : ''}{f.DisplayName}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Turn on the features members on this tier can use.</p>
            </div>

            {/* Usage Limits — per-feature monthly quota, grouped by audience the same
                way "Unlocks features" is, so a tier only fills in the limits that
                apply to who it's sold to. A group the tier isn't available to is
                greyed out and disabled. */}
            <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Usage limits <span className="font-normal text-xs opacity-70">(per month · blank = unchanged)</span>
                </label>
                <div className="space-y-2.5">
                    {[...new Set(FEATURE_LIMIT_DEFS.map((f) => f.audience))].sort().map((aud) => {
                        const defs = FEATURE_LIMIT_DEFS.filter((f) => f.audience === aud);
                        // Some features are access-gated rather than metered — opening a
                        // report or a settings page isn't a countable "use" — so their
                        // fields stay read-only no matter which audience the tier targets.
                        const metered = defs.some((f) => f.metered !== false);
                        const applies = metered && audienceNames.includes(aud);
                        const note = !metered
                            ? '— on/off only, set under Unlocks features'
                            : (!applies ? '— not available to this tier' : '');
                        return (
                            <div key={aud}>
                                <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    {aud}
                                    {note && (
                                        <span className="ml-2 font-normal normal-case opacity-70">{note}</span>
                                    )}
                                </div>
                                <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${applies ? '' : 'opacity-50'}`}>
                                    {defs.map((f) => (
                                        <div key={f.key}>
                                            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{f.label}</label>
                                            <input
                                                type="number"
                                                min="0"
                                                disabled={!applies}
                                                value={applies ? limits[f.key] : ''}
                                                onChange={(e) => setLimits({ ...limits, [f.key]: e.target.value })}
                                                className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'} ${applies ? '' : 'cursor-not-allowed'}`}
                                                placeholder={applies ? 'e.g. 3' : '—'}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Duration (days)</label>
                    <input
                        type="number"
                        value={formData.duration_days}
                        min="1"
                        onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                    />
                    {durationProblem && <p className="mt-1 text-xs font-medium text-red-500">{durationProblem}</p>}
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Trial Days <span className="font-normal text-xs opacity-70">(e.g. Beta: 90)</span>
                    </label>
                    <input
                        type="number"
                        value={formData.trial_days}
                        min="0"
                        onChange={(e) => setFormData({ ...formData, trial_days: e.target.value ? parseInt(e.target.value) : '' })}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                        placeholder="Leave blank for no trial"
                    />
                    {trialProblem && <p className="mt-1 text-xs font-medium text-red-500">{trialProblem}</p>}
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Price ($)</label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                    />
                    {priceProblem && <p className="mt-1 text-xs font-medium text-red-500">{priceProblem}</p>}
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Billing Frequency</label>
                    <select
                        value={formData.billing_frequency}
                        onChange={(e) => setFormData({ ...formData, billing_frequency: e.target.value })}
                        disabled={priceIsZero}
                        className={`select-styled w-full text-sm font-medium rounded-xl px-3 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'} ${priceIsZero ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                        {/* <option value="one-time">One-time</option> */}
                        {/* Checkout only sells subscriptions; One-time only shows on an old tier that has it */}
                        {formData.billing_frequency === 'one-time' && <option value="one-time">One-time</option>}
                    </select>
                </div>
                <div className="md:col-span-2">
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Price ID {isFreeForever ? '' : '*'}
                    </label>
                    <input
                        type="text"
                        value={formData.payment_link}
                        onChange={(e) => setFormData({ ...formData, payment_link: e.target.value })}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                        placeholder={isFreeForever ? 'Not needed for a free tier' : 'price_1A2b3C...'}
                    />
                </div>
            </div>
            <div>
                <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Description</label>
                <textarea
                    value={formData.tier_description}
                    onChange={(e) => setFormData({ ...formData, tier_description: e.target.value })}
                    className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                    rows="3"
                    placeholder="Describe this membership tier..."
                />
            </div>

            {/* Group pricing — organizations whose licenses grant this tier can buy
                more seats at this price (request -> BizBrand approval -> payment). */}
            {/* Sell to organizations (group pricing) */}
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <label className={`flex items-center gap-2 text-sm font-semibold ${orgBlockedReason ? 'opacity-50' : ''} ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <input type="checkbox" checked={groupOn} disabled={!!orgBlockedReason}
                           onChange={(e) => setGroup({ ...group, enabled: e.target.checked })} />
                    <Building2 size={16} /> Sell to organizations
                </label>
                <p className={`mt-1 ml-6 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {orgBlockedReason || (
                        <>Organizations whose licenses give this tier pay for the seats you set up for them (Organizations → License requests).
                        {' '}Each seat costs this tier's price (<span className="font-semibold">${planPrice.toFixed(2)} / {perPeriod}</span>), less a volume discount.</>
                    )}
                </p>
                {groupOn && (
                    <div className="mt-4 space-y-4">
                        <div>
                            <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                Volume discounts <span className="font-normal text-xs opacity-70">(buy more, pay less)</span>
                            </label>
                            <div className="space-y-2">
                                {group.discounts.map((d, i) => (
                                    <div key={i} className={`flex flex-wrap items-center gap-2 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                        <input type="number" min="2" step="1" value={d.min_quantity} aria-label="Minimum licenses" placeholder="10"
                                               onChange={(e) => setDiscount(i, { min_quantity: e.target.value })} className={boxCls("w-16")} />
                                        <span className="whitespace-nowrap">+ licenses →</span>
                                        <input type="number" min="0" max="99.99" step="0.01" aria-label="Percent off" placeholder="10"
                                               value={d.pctText ?? (d.pct ? pctText(d.pct) : '')}
                                               onChange={(e) => {
                                                   const pct = parseFloat(e.target.value) || 0;
                                                   setDiscount(i, { pct, pctText: e.target.value, priceText: undefined });
                                               }}
                                               className={boxCls("w-20")} />
                                        <span className="whitespace-nowrap">% off</span>
                                        <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>or</span>
                                        <span>$</span>
                                        <input type="number" min="0" step="0.01" aria-label="Price per license after the discount"
                                               placeholder={planPrice ? priceAfter(10).toFixed(2) : ''}
                                               value={d.priceText ?? (d.pct ? priceAfter(d.pct).toFixed(2) : '')}
                                               onChange={(e) => {
                                                   const each = parseFloat(e.target.value);
                                                   const pct = planPrice && !Number.isNaN(each) ? (1 - each / planPrice) * 100 : 0;
                                                   setDiscount(i, { pct, priceText: e.target.value, pctText: undefined });
                                               }}
                                               className={boxCls("w-24")} />
                                        <span>each</span>
                                        <input type="text" aria-label="Stripe Price ID for this price" placeholder="Stripe Price ID (price_...)"
                                               value={d.stripe_price_id || ''}
                                               onChange={(e) => setDiscount(i, { stripe_price_id: e.target.value })}
                                               className={`${boxCls("w-56")} font-mono text-xs`} />
                                        <button type="button" aria-label="Remove discount"
                                                onClick={() => setGroup((g) => ({ ...g, discounts: g.discounts.filter((_, j) => j !== i) }))}
                                                className={`p-1.5 rounded-lg ${isDarkMode ? 'text-slate-400 hover:text-red-400 hover:bg-slate-700' : 'text-slate-500 hover:text-red-600 hover:bg-red-50'}`}>
                                            <Trash2 size={16} />
                                        </button>
                                        {discountProblems[i] && <p className="basis-full text-xs font-medium text-red-500">{discountProblems[i]}</p>}
                                    </div>
                                ))}
                            </div>
                            <button type="button" onClick={() => setGroup((g) => ({ ...g, discounts: [...g.discounts, { min_quantity: '', pct: 0, stripe_price_id: '' }] }))}
                                    className={`mt-2 inline-flex items-center gap-1 text-sm font-semibold ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}>
                                <Plus size={14} /> Add discount
                            </button>
                            <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Type a percent off or the price each; the other box follows. An order gets the biggest discount it qualifies for.
                                Example: with 10+ = 10% off and 50+ = 20% off, 30 licenses get 10% off.
                                Each discounted price needs its own Stripe price: create one in Stripe for that price per license (same billing period) and paste its ID.
                                Seats without a discount use this tier's Price ID.
                            </p>
                        </div>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            Example: {exampleQty} licenses = <span className="font-semibold">${exampleTotal.toFixed(2)}</span> per {perPeriod}
                            {examplePct > 0 ? ` (${pctText(examplePct)}% off)` : ''}.
                        </p>
                    </div>
                )}
            </div>
            <div className="flex flex-wrap gap-6">
                <label className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <input
                        type="checkbox"
                        checked={formData.show_in_onboarding}
                        onChange={(e) => setFormData({ ...formData, show_in_onboarding: e.target.checked })}
                        className="w-4 h-4"
                    />
                    Show in onboarding
                </label>
                <label className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <input
                        type="checkbox"
                        checked={formData.open_for_enrollment}
                        onChange={(e) => setFormData({ ...formData, open_for_enrollment: e.target.checked })}
                        className="w-4 h-4"
                    />
                    Open for new enrollment
                    <span className="font-normal text-xs opacity-70">(turn off to stop new signups without affecting existing members)</span>
                </label>
                <label className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <input
                        type="checkbox"
                        checked={formData.is_most_popular}
                        onChange={(e) => setFormData({ ...formData, is_most_popular: e.target.checked })}
                        className="w-4 h-4"
                    />
                    Mark as “Most Popular”
                    <span className="font-normal text-xs opacity-70">(highlights this tier + shows the badge on the plans page)</span>
                </label>
                <label className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <input
                        type="checkbox"
                        checked={formData.is_referral_reward}
                        onChange={(e) => setFormData({ ...formData, is_referral_reward: e.target.checked })}
                        disabled={!formData.is_referral_reward && Boolean(rewardBlockedReason)}
                        className="w-4 h-4"
                    />
                    Referral reward tier
                </label>
                {/* Exactly one tier holds this, so say which one has it now —
                    ticking it here moves it off that tier. */}
                <p className={`-mt-1 ml-6 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    The tier a friend referral grants both people a week of. Only one tier can hold this
                    {rewardHolder ? <> — currently <span className="font-semibold">{rewardHolder.TierName}</span>; ticking this moves it here.</> : '.'}
                </p>
                {rewardBlockedReason && (
                    <p className={`-mt-1 ml-6 text-xs ${formData.is_referral_reward
                        ? (isDarkMode ? 'text-red-300' : 'text-red-600')
                        : (isDarkMode ? 'text-slate-400' : 'text-slate-500')}`}>
                        {rewardBlockedReason}
                    </p>
                )}
            </div>
            {error && (
                <div role="alert" className={`p-3 rounded-lg text-sm border ${isDarkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
                    {error}
                </div>
            )}
            <div className="flex gap-2 pt-2">
                <button
                    onClick={submit}
                    disabled={saving || !formData.tier_name.trim() || audienceIds.length === 0 || (!isFreeForever && !formData.payment_link.trim())
                        || Boolean(priceProblem || durationProblem || trialProblem) || (groupOn && discountProblems.some(Boolean))}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
                >
                    <Save size={18} />
                    {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                    onClick={onCancel}
                    className={`flex items-center gap-2 px-4 py-2 border rounded-lg ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
                >
                    <X size={18} />
                    Cancel
                </button>
            </div>
        </div>
    );
}

//function CodeEditForm({ code, tiers, onSave, onCancel, saving }) {
function CodeEditForm({ code, tiers, onSave, onCancel, saving, error }) {
    const { isDarkMode } = useTheme();
    const [formData, setFormData] = React.useState({
        code: code?.Code || '',
        discount_type: code?.DiscountType || 'free',
        discount_value: code?.DiscountValue != null ? Number(code.DiscountValue) : '',
        max_redemptions: code?.MaxRedemptions != null ? Number(code.MaxRedemptions) : '',
        expires_at: code?.ExpiresAt ? String(code.ExpiresAt).slice(0, 10) : '',
        is_active: code?.isActive !== undefined ? (code.isActive ? 1 : 0) : 1,
    });
    const [tierIds, setTierIds] = React.useState((code?.Tiers || []).map(t => t.TierID));

    const toggleTier = (id) => {
        setTierIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const submit = () => onSave({
        code: formData.code ? formData.code.trim().toUpperCase() : '',
        tier_ids: tierIds,
        discount_type: formData.discount_type,
        discount_value: formData.discount_type === 'free' ? null : (formData.discount_value === '' ? null : Number(formData.discount_value)),
        max_redemptions: formData.max_redemptions === '' ? null : parseInt(formData.max_redemptions),
        expires_at: formData.expires_at || null,
        is_active: formData.is_active,
    });

    const needsValue = formData.discount_type !== 'free';
    // Same rules as the server: more than 0, and a percent at most 100
    const discountNum = Number(formData.discount_value);
    const discountProblem = !needsValue || formData.discount_value === '' ? ''
        : !(discountNum > 0) ? 'The discount has to be more than 0.'
            : formData.discount_type === 'percent' && discountNum > 100 ? "A percent discount can't be more than 100%." : '';
    // Only active tiers are grantable; keep any tier already on the code even if
    // it's since gone inactive.
    const grantableTiers = (tiers || []).filter(t => t.IsActive || tierIds.includes(t.TierID));

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Code <span className="font-normal text-xs opacity-70">(blank = auto-generate)</span>
                    </label>
                    <input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        disabled={!!code}
                        className={`w-full p-2 border rounded-lg font-mono ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'} ${code ? 'opacity-60' : ''}`}
                        placeholder="e.g., COACHFREE"
                    />
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Status</label>
                    <select
                        value={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                        className={`select-styled w-full text-sm font-medium rounded-xl px-3 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'border-slate-300 bg-white text-slate-700'}`}
                    >
                        <option value={1}>Active</option>
                        <option value={0}>Inactive</option>
                    </select>
                </div>
                <div className="md:col-span-2">
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Applies to tiers *</label>
                    <div className="flex flex-wrap gap-2">
                        {grantableTiers.map(t => {
                            const on = tierIds.includes(t.TierID);
                            return (
                                <button
                                    type="button"
                                    key={t.TierID}
                                    onClick={() => toggleTier(t.TierID)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                                        on
                                            ? 'bg-blue-600 border-blue-600 text-white'
                                            : isDarkMode ? 'border-slate-600 text-slate-300 hover:border-slate-500' : 'border-slate-300 text-slate-600 hover:border-slate-400'
                                    }`}
                                >
                                    {on ? '✓ ' : ''}{t.TierName}{t.Audiences && t.Audiences.length ? ` (${t.Audiences.join(', ')})` : ''}
                                </button>
                            );
                        })}
                        {grantableTiers.length === 0 && (
                            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No active tiers.</span>
                        )}
                    </div>
                    <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Pick one or more active tiers. A coach redeeming a free code gets a Coach tier from this set.</p>
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Discount Type</label>
                    <select
                        value={formData.discount_type}
                        onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                        className={`select-styled w-full text-sm font-medium rounded-xl px-3 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'border-slate-300 bg-white text-slate-700'}`}
                    >
                        <option value="free">Free (full grant)</option>
                        <option value="percent">Percent off</option>
                        <option value="amount">Amount off</option>
                    </select>
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {formData.discount_type === 'percent' ? 'Percent (%)' : formData.discount_type === 'amount' ? 'Amount ($)' : 'Discount value'} {needsValue ? '*' : ''}
                    </label>
                    <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={formData.discount_type === 'percent' ? 100 : undefined}
                        value={needsValue ? formData.discount_value : ''}
                        disabled={!needsValue}
                        onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                        placeholder={needsValue ? '' : 'Not needed for a free code'}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'} ${!needsValue ? 'opacity-50 cursor-not-allowed bg-slate-100' : ''}`}
                    />
                    {discountProblem && <p className="mt-1 text-xs font-medium text-red-500">{discountProblem}</p>}
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {/* Expires <span className="font-normal text-xs opacity-70">(blank = never)</span> */}
                        Expires <span className="font-normal text-xs opacity-70">(works through the end of that day, Central time · blank = never)</span>
                    </label>
                    <input
                        type="date"
                        value={formData.expires_at}
                        onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                    />
                </div>
                <div>
                    <label className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Max Redemptions <span className="font-normal text-xs opacity-70">(blank = unlimited)</span>
                    </label>
                    <input
                        type="number"
                        value={formData.max_redemptions}
                        onChange={(e) => setFormData({ ...formData, max_redemptions: e.target.value })}
                        className={`w-full p-2 border rounded-lg ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'border-slate-300'}`}
                        placeholder="Unlimited"
                    />
                </div>
            </div>
            {error && (
                <div role="alert" className={`p-3 rounded-lg text-sm border ${isDarkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
                    {error}
                </div>
            )}
            <div className="flex gap-2 pt-2">
                <button
                    onClick={submit}
                    //disabled={saving || tierIds.length === 0 || (needsValue && formData.discount_value === '')}
                    disabled={saving || tierIds.length === 0 || (needsValue && formData.discount_value === '') || Boolean(discountProblem)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
                >
                    <Save size={18} />
                    {saving ? 'Saving...' : 'Save'}
                </button>
                <button onClick={onCancel} className={`flex items-center gap-2 px-4 py-2 border rounded-lg ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}>
                    <X size={18} />
                    Cancel
                </button>
            </div>
        </div>
    );
}
